import type { LeadershipMetrics } from '@delivery-metrics/shared';
import { TrendingUp } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import React from 'react';

import { MetricCard } from '../MetricCard';

interface ExecutiveKPISectionProps {
  data: LeadershipMetrics | null;
  loading?: boolean;
}

export const ExecutiveKPISection: React.FC<ExecutiveKPISectionProps> = ({
  data,
  loading = false,
}) => {
  // Format FTE time investment for display
  const formatTimeInvestment = (
    hours: number,
    weeks: number,
    months: number
  ) => {
    if (months >= 1) {
      return `${months.toFixed(1)} months`;
    } else if (weeks >= 1) {
      return `${weeks.toFixed(1)} weeks`;
    } else {
      return `${hours.toFixed(0)} hours`;
    }
  };

  const avgTimeInvestment = data?.kpis?.averageFeatureTimeInvestment;
  const timeInvestmentDisplay = avgTimeInvestment
    ? formatTimeInvestment(
        avgTimeInvestment.hours,
        avgTimeInvestment.weeks,
        avgTimeInvestment.months
      )
    : '0 hours';

  return (
    <>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <TrendingUp sx={{ mr: 1 }} />
        Executive KPIs
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard
            title='Avg Feature Time Investment'
            value={avgTimeInvestment?.hours || 0}
            customDisplay={timeInvestmentDisplay}
            trend={avgTimeInvestment?.trend}
            description='FTE time per feature'
            tooltip='Average FTE time (hours/weeks/months) required to deliver a complete feature including development, testing, and deployment'
            color='primary'
            size='medium'
            loading={loading}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard
            title='Features Delivered'
            value={data?.kpis?.featuresDelivered?.value || 0}
            trend={data?.kpis?.featuresDelivered?.trend}
            description='This period'
            tooltip='Total number of features successfully delivered to production'
            color='success'
            size='medium'
            loading={loading}
          />
        </Grid>
      </Grid>
    </>
  );
};
